import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, message, Modal, Select, Switch, Table, Tabs } from 'antd';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../services/api';
import { FilePdfOutlined, LogoutOutlined, PlusOutlined } from '@ant-design/icons';
import { formatCurrencySimple } from '../utils/currency';
import { DEFAULT_CURRENCY_CODE } from '../constants/localeOptions';
import { downloadStaffPaymentReceiptPdf } from '../utils/staffPaymentReceipt';

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [studioOwners, setStudioOwners] = useState<any[]>([]);
  const [producers, setProducers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [staffPayments, setStaffPayments] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);

  const [studioOwnerModalOpen, setStudioOwnerModalOpen] = useState(false);
  const [studioOwnerEditing, setStudioOwnerEditing] = useState<any | null>(null);
  const [studioOwnerForm] = Form.useForm();
  const [studioFilter, setStudioFilter] = useState<number | 'all'>('all');
  const [studioSearch, setStudioSearch] = useState<string>('');

  const getErrorMessage = (error: any, fallback: string) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) {
      return detail;
    }
    const data = error?.response?.data;
    if (data && typeof data === 'object') {
      const firstKey = Object.keys(data)[0];
      const firstValue = firstKey ? data[firstKey] : null;
      if (Array.isArray(firstValue) && firstValue.length > 0) {
        return `${firstKey}: ${firstValue[0]}`;
      }
      if (typeof firstValue === 'string') {
        return `${firstKey}: ${firstValue}`;
      }
    }
    if (typeof error?.message === 'string' && error.message.trim().length > 0) {
      return error.message;
    }
    return fallback;
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ownersRes, producersRes, projectsRes, paymentsRes, expensesRes] = await Promise.all([
        superAdminAPI.getStudioOwners(),
        superAdminAPI.getStaff(),
        superAdminAPI.getProjects(),
        superAdminAPI.getPayments(),
        superAdminAPI.getExpenses(),
      ]);
      setStudioOwners(ownersRes.data || []);
      setProducers(producersRes.data || []);
      setProjects(projectsRes.data || []);
      setPayments(paymentsRes.data || []);
      setExpenses(expensesRes.data || []);

      try {
        const staffPaymentsRes = await superAdminAPI.getStaffPayments();
        setStaffPayments(staffPaymentsRes.data || []);
      } catch (staffPaymentError: any) {
        setStaffPayments([]);
        const statusCode = staffPaymentError?.response?.status;
        if (statusCode === 404) {
          message.warning('Staff payout records are unavailable until the backend is updated and restarted.');
        } else {
          message.warning(getErrorMessage(staffPaymentError, 'Failed to load staff payout records'));
        }
      }
    } catch (error) {
      message.error(getErrorMessage(error, 'Failed to load system data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const studioOptions = [{ label: 'All Studios', value: 'all' as const }, ...studioOwners.map((o) => ({ label: o.studio_name || o.username || `Studio ${o.id}`, value: o.id }))];

  const filteredStudioOwners = studioOwners.filter((o) => {
    if (studioFilter !== 'all' && o.id !== studioFilter) return false;
    if (!studioSearch || studioSearch.trim() === '') return true;
    const q = studioSearch.toLowerCase();
    return (
      (o.studio_name || '').toLowerCase().includes(q) ||
      (o.username || '').toLowerCase().includes(q) ||
      (`${o.first_name || ''} ${o.last_name || ''}`).toLowerCase().includes(q) ||
      (o.email || '').toLowerCase().includes(q)
    );
  });

  const filteredProducers = producers.filter((p) => (studioFilter === 'all' ? true : p.studio_owner === studioFilter));
  const filteredProjects = projects.filter((p) => (studioFilter === 'all' ? true : p.user === studioFilter));
  const filteredPayments = payments.filter((p) => (studioFilter === 'all' ? true : p.user === studioFilter));
  const filteredStaffPayments = staffPayments.filter((p) => (studioFilter === 'all' ? true : p.studio_owner === studioFilter));
  const filteredExpenses = expenses.filter((e) => (studioFilter === 'all' ? true : e.user === studioFilter));
  const approvedStaffPayoutCount = filteredStaffPayments.filter((payment: any) => payment.status === 'approved').length;

  const handleLogout = () => {
    localStorage.removeItem('super_access_token');
    localStorage.removeItem('super_refresh_token');
    localStorage.removeItem('super_user');
    navigate('/super-admin/login');
  };

  const openStudioOwnerModal = (record?: any) => {
    setStudioOwnerEditing(record || null);
    setStudioOwnerModalOpen(true);
    studioOwnerForm.setFieldsValue({
      ...record,
      password: '',
    });
  };

  const handleSaveStudioOwner = async () => {
    try {
      const values = await studioOwnerForm.validateFields();
      if (studioOwnerEditing) {
        await superAdminAPI.updateStudioOwner(studioOwnerEditing.id, values);
        message.success('Studio owner updated');
      } else {
        await superAdminAPI.createStudioOwner(values);
        message.success('Studio owner created');
      }
      setStudioOwnerModalOpen(false);
      studioOwnerForm.resetFields();
      await loadAll();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(getErrorMessage(error, 'Failed to save studio owner'));
    }
  };

  const confirmDelete = (action: () => Promise<void>, label: string) => {
    Modal.confirm({
      title: `Delete ${label}?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: action,
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-8 shadow-lg">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="mb-3 flex items-center gap-3">
                <img src="/SOMS_logo.png" alt="SOMS" className="h-10 w-10 rounded-lg" />
                <h1 className="text-3xl font-bold text-white">System Admin Portal</h1>
              </div>
              <p className="text-sm text-slate-300">Manage all studio operations, users, and system resources</p>
            </div>
            <Button
              type="default"
              danger
              size="large"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="w-full sm:w-auto"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
          {[
            {
              title: 'Total Studios',
              value: studioOwners.length,
              accent: 'from-blue-500 to-blue-600',
              icon: 'ST',
            },
            {
              title: 'Team Members',
              value: producers.length,
              accent: 'from-purple-500 to-purple-600',
              icon: 'TM',
            },
            {
              title: 'Active Projects',
              value: projects.filter((p: any) => p.status !== 'delivered').length,
              accent: 'from-indigo-500 to-indigo-600',
              icon: 'PJ',
            },
            {
              title: 'Payments',
              value: payments.length,
              accent: 'from-emerald-500 to-emerald-600',
              icon: 'PM',
            },
            {
              title: 'Staff Payouts',
              value: staffPayments.length,
              accent: 'from-teal-500 to-teal-600',
              icon: 'PO',
            },
            {
              title: 'Expenses',
              value: expenses.length,
              accent: 'from-orange-500 to-orange-600',
              icon: 'EX',
            },
          ].map((card) => (
            <div
              key={card.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${card.accent}`} />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {card.title}
                  </p>
                  <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                    {card.value}
                  </div>
                </div>
                <div className="text-3xl opacity-20">{card.icon}</div>
              </div>
            </div>
          ))}
        </section>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-end gap-3 p-4">
            <div className="flex w-full max-w-sm items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Filter by Studio</label>
              <Select
                style={{ minWidth: 220 }}
                value={studioFilter}
                options={studioOptions}
                onChange={(val) => setStudioFilter(val as any)}
                showSearch
                optionFilterProp="label"
              />
            </div>
          </div>
          <Tabs
            defaultActiveKey="studioOwners"
            tabBarStyle={{
              borderBottom: '1px solid var(--tw-border-color)',
              marginBottom: 0,
              padding: '0 1.5rem',
            }}
          >
            <Tabs.TabPane tab={<span className="font-semibold">Studio Owners ({studioOwners.length})</span>} key="studioOwners">
              <div className="space-y-4 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Manage Studio Owners</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Create, edit, and manage all studio owner accounts</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input.Search
                      placeholder="Search studio owners"
                      allowClear
                      value={studioSearch}
                      onChange={(e) => setStudioSearch((e.target as HTMLInputElement).value)}
                      style={{ width: 280 }}
                    />
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => openStudioOwnerModal()}>
                      Add Studio Owner
                    </Button>
                  </div>
                </div>
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={filteredStudioOwners}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  columns={[
                    { title: 'Studio Name', dataIndex: 'studio_name', sorter: (a, b) => a.studio_name.localeCompare(b.studio_name) },
                    { title: 'Username', dataIndex: 'username' },
                    { title: 'Owner', render: (_, record) => `${record.first_name} ${record.last_name}` },
                    { title: 'Email', dataIndex: 'email' },
                    {
                      title: 'Plan',
                      dataIndex: 'subscription_plan',
                      render: (val) => (
                        <span
                          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                          style={{
                            backgroundColor: val === 'professional' ? '#dbeafe' : val === 'basic' ? '#ddd6fe' : '#f3f4f6',
                            color: val === 'professional' ? '#1e40af' : val === 'basic' ? '#5b21b6' : '#374151',
                          }}
                        >
                          {val}
                        </span>
                      )
                    },
                    {
                      title: 'Status',
                      render: (_, record) => (
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${record.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {record.is_active ? 'Active' : 'Inactive'}
                        </span>
                      )
                    },
                    {
                      title: 'Actions',
                      width: 280,
                      render: (_, record) => (
                        <div className="flex flex-wrap gap-2">
                          <Button size="small" onClick={() => openStudioOwnerModal(record)}>Edit</Button>
                          <Button
                            size="small"
                            danger
                            onClick={() =>
                              confirmDelete(async () => {
                                await superAdminAPI.deleteStudioOwner(record.id);
                                await loadAll();
                              }, 'studio owner')
                            }
                          >
                            Delete
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={<span className="font-semibold">Team Members ({producers.length})</span>} key="producers">
              <div className="space-y-4 p-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">View Team Members</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">View all producers and staff accounts across studios</p>
                </div>
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={filteredProducers}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  columns={[
                    { title: 'Name', render: (_, record) => `${record.first_name} ${record.last_name}`, sorter: (a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`) },
                    { title: 'Email', dataIndex: 'email' },
                      { title: 'Role', render: (_, record) => record.role === 'producer' ? 'Producer' : record.staff_title || 'Staff' },
                      { title: 'Studio', render: (_, record) => studioOwners.find((o) => o.id === record.studio_owner)?.studio_name || '-' },
                      { title: 'Commission / Salary', render: (_, record) => record.role === 'producer' ? `${record.commission_rate || 0}%` : (record.salary ? `${record.salary_currency || '$'}${record.salary}` : 'N/A'), align: 'center' },
                    {
                      title: 'Status',
                      render: (_, record) => (
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${record.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {record.is_active ? 'Active' : 'Inactive'}
                        </span>
                      )
                    },
                  ]}
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={<span className="font-semibold">Projects ({projects.length})</span>} key="projects">
              <div className="space-y-4 p-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">View Projects</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">View all studio projects and their status</p>
                </div>
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={filteredProjects}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Song Title', dataIndex: 'song_title' },
                    { title: 'Client', dataIndex: 'client_name' },
                    { title: 'Phone', render: (_, record) => `${record.client_phone_country_code || ''} ${record.client_phone || '-'}`.trim() || '-' },
                    { title: 'Currency', dataIndex: 'currency_code', align: 'center' },
                    { title: 'Agreed Price', render: (_, record) => formatCurrencySimple(record.agreed_price, record.currency_code), align: 'right' },
                    { title: 'Studio', render: (_, record) => studioOwners.find((o) => o.id === record.user)?.studio_name || '-' },
                    { title: 'Commission', render: (_, record) => formatCurrencySimple(record.producer_commission, record.currency_code), align: 'right' },
                    {
                      title: 'Status',
                      render: (_, record) => (
                        <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: record.status === 'delivered' ? '#dcfce7' : record.status === 'mastering' ? '#fef3c7' : '#e0e7ff', color: record.status === 'delivered' ? '#15803d' : record.status === 'mastering' ? '#b45309' : '#3730a3' }}>
                          {record.status}
                        </span>
                      )
                    },
                  ]}
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={<span className="font-semibold">Payments ({payments.length})</span>} key="payments">
              <div className="space-y-4 p-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">View Payments</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Track all payment transactions across studios</p>
                </div>
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={filteredPayments}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Project', dataIndex: 'project_name' },
                    { title: 'Currency', render: (_, record) => record.currency_code || record.project_currency_code || '-', align: 'center' },
                    { title: 'Amount', render: (_, record) => formatCurrencySimple(record.amount, record.currency_code || record.project_currency_code), align: 'right' },
                    { title: 'Method', dataIndex: 'payment_method' },
                    { title: 'Studio', render: (_, record) => studioOwners.find((o) => o.id === record.user)?.studio_name || '-' },
                  ]}
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={<span className="font-semibold">Staff Payouts ({staffPayments.length})</span>} key="staffPayouts">
              <div className="space-y-5 p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Producer & Staff Payouts</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">System-wide visibility into owner-recorded staff payments and generated receipts.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Filtered Records</p>
                      <p className="mt-1 text-lg font-semibold">{filteredStaffPayments.length}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Approved</p>
                      <p className="mt-1 text-lg font-semibold text-emerald-600">{approvedStaffPayoutCount}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Pending</p>
                      <p className="mt-1 text-lg font-semibold text-amber-600">{filteredStaffPayments.filter((p: any) => p.status === 'pending').length}</p>
                    </div>
                  </div>
                </div>
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={filteredStaffPayments}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Receipt', dataIndex: 'receipt_number', width: 190, render: (value, record) => value || `Receipt #${record.id}` },
                    { title: 'Studio', render: (_, record) => record.studio_name || studioOwners.find((o) => o.id === record.studio_owner)?.studio_name || '-' },
                    { title: 'Recipient', render: (_, record) => record.recipient_full_name || record.recipient_name || '-' },
                    { title: 'Role', render: (_, record) => record.recipient_role === 'producer' ? 'Producer' : 'Staff' },
                    { title: 'Amount', render: (_, record) => formatCurrencySimple(record.amount, record.currency_code || DEFAULT_CURRENCY_CODE), align: 'right' },
                    { title: 'Paid To Date', render: (_, record) => formatCurrencySimple(record.paid_to_date, record.currency_code || DEFAULT_CURRENCY_CODE), align: 'right' },
                    { title: 'Remaining', render: (_, record) => formatCurrencySimple(record.remaining_balance, record.currency_code || DEFAULT_CURRENCY_CODE), align: 'right' },
                    { title: 'Date', dataIndex: 'payment_date' },
                    {
                      title: 'Status',
                      render: (_, record) => {
                        const statusClasses: any = {
                          approved: 'bg-emerald-100 text-emerald-800',
                          pending: 'bg-amber-100 text-amber-800',
                          rejected: 'bg-rose-100 text-rose-800',
                        };
                        return (
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[record.status] || 'bg-slate-100 text-slate-700'}`}>
                            {String(record.status || 'pending').toUpperCase()}
                          </span>
                        );
                      },
                    },
                    {
                      title: 'Receipt PDF',
                      fixed: 'right' as const,
                      render: (_, record) => (
                        <Button
                          size="small"
                          icon={<FilePdfOutlined />}
                          onClick={() => downloadStaffPaymentReceiptPdf(record, {
                            studioName: record.studio_name,
                            recipientName: record.recipient_full_name || record.recipient_name,
                            paidToDate: record.paid_to_date,
                            remainingBalance: record.remaining_balance,
                          })}
                        >
                          Download
                        </Button>
                      ),
                    },
                  ]}
                />
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab={<span className="font-semibold">Expenses ({expenses.length})</span>} key="expenses">
              <div className="space-y-4 p-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">View Expenses</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Track all studio expenses across the system</p>
                </div>
                <Table
                  rowKey="id"
                  loading={loading}
                  dataSource={filteredExpenses}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  columns={[
                    { title: 'Description', dataIndex: 'description' },
                    { title: 'Amount', render: (_, record) => `$${record.amount}`, align: 'right' },
                    { title: 'Category', dataIndex: 'category' },
                    { title: 'Studio', render: (_, record) => studioOwners.find((o) => o.id === record.user)?.studio_name || '-' },
                  ]}
                />
              </div>
            </Tabs.TabPane>
          </Tabs>
        </div>
      </main>

      <Modal
        title={studioOwnerEditing ? 'Edit Studio Owner' : 'Add Studio Owner'}
        open={studioOwnerModalOpen}
        onCancel={() => setStudioOwnerModalOpen(false)}
        onOk={handleSaveStudioOwner}
        okText="Save"
        okButtonProps={{ size: 'large' }}
        cancelButtonProps={{ size: 'large' }}
        width={600}
      >
        <Form layout="vertical" form={studioOwnerForm}>
          <Form.Item label="Studio Name" name="studio_name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="First Name" name="first_name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Last Name" name="last_name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Username" name="username">
            <Input />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input />
          </Form.Item>
          <Form.Item label="Subscription Plan" name="subscription_plan" rules={[{ required: true }]}>
            <Select
              options={[
                { label: 'Free', value: 'free' },
                { label: 'Basic', value: 'basic' },
                { label: 'Professional', value: 'professional' },
              ]}
            />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Staff" name="is_staff" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Superuser" name="is_superuser" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SuperAdminDashboard;




